// A contractor's insurance / accreditation: kind, reference, cover, dates and the certificate itself. Used by staff
// (recorded as verified) and by the contractor in their portal (waits for Lotus Living to check it).
import { useState } from 'react';
import { Button, Field, Input, Select } from '../ui';
import { toUpload } from '../../lib/resident';
import { COMPLIANCE_KINDS, type ComplianceInput, type ComplianceKind } from '../../lib/maintenance';

export function ComplianceForm({ onSubmit, onCancel, pending, error, fileRequired, submitLabel = 'Save', initialKind = 'PUBLIC_LIABILITY' }: {
  onSubmit: (input: ComplianceInput) => void; onCancel: () => void; pending?: boolean; error?: string | null; fileRequired?: boolean; submitLabel?: string; initialKind?: ComplianceKind;
}) {
  const [kind, setKind] = useState<ComplianceKind>(initialKind);
  const [label, setLabel] = useState('');
  const [reference, setReference] = useState('');
  const [cover, setCover] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const insurance = kind === 'PUBLIC_LIABILITY' || kind === 'EMPLOYERS_LIABILITY';

  const submit = async () => {
    setBusy(true);
    try {
      onSubmit({
        kind, label: kind === 'OTHER' ? label.trim() : null, reference: reference.trim() || null,
        coverMinor: insurance && cover ? Math.round(Number(cover) * 100) : null, issuedOn: issuedOn || null, expiresOn: expiresOn || null,
        file: file ? await toUpload(file) : null,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-xl border border-ink-100 bg-ink-50 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="What it is"><Select value={kind} onChange={(e) => setKind(e.target.value as ComplianceKind)} className="w-full">{COMPLIANCE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
        {kind === 'OTHER' && <Field label="Accreditation"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. BAFE SP203, LEIA" className="w-full" /></Field>}
        <Field label={insurance ? 'Policy number' : 'Registration number'}><Input value={reference} onChange={(e) => setReference(e.target.value)} className="w-full" /></Field>
        {insurance && <Field label="Cover (£)"><Input type="number" min="0" value={cover} onChange={(e) => setCover(e.target.value)} placeholder="5000000" className="w-full" /></Field>}
        <Field label="Issued"><Input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} className="w-full" /></Field>
        <Field label="Runs to"><Input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} className="w-full" /></Field>
      </div>
      <Field label="Certificate or schedule" hint="PDF or a photo. Photos are shrunk to 1600 px in your browser before upload.">
        <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="block w-full text-[12.5px] text-ink-600 file:mr-3 file:rounded-lg file:border-0 file:bg-ink-100 file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-ink-800 hover:file:bg-ink-200" />
      </Field>
      {error && <p className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button size="sm" disabled={pending || busy || (fileRequired && !file) || (kind === 'OTHER' && !label.trim())} onClick={() => void submit()}>{pending || busy ? 'Saving…' : submitLabel}</Button>
      </div>
    </div>
  );
}
